import { ArrowLeft } from "lucide-react";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { LearningChatComposer } from "./LearningChatComposer.js";
import { LearningChatClarifyOptions, LearningChatThinkingPanel } from "./LearningChatPanels.js";
import { LearningChatResultPanel } from "./LearningChatResultPanel.js";
import { clarifyOptionKey } from "./learning-chat-flow.js";
import type { ChatSession, ThreadMessage } from "./learning-chat-mock.js";

type LearningChatSessionProps = {
  session: ChatSession;
  draft: string;
  onDraftChange: (value: string) => void;
  onBack: () => void;
  onAnswer: (text: string) => void;
  bottomRef: RefObject<HTMLDivElement | null>;
};

export function LearningChatSession({
  session,
  draft,
  onDraftChange,
  onBack,
  onAnswer,
  bottomRef,
}: LearningChatSessionProps) {
  const { t } = useTranslation();
  const showThinking = session.phase === "thinking" || session.phase === "clarify";

  return (
    // Intentional (accepted): -mb-28 cancels shell mobile bottom padding so the session
    // fills the viewport under the topbar. Sitting partly under the fixed mobile nav on
    // small screens is an accepted prototype trade-off, not a layout bug to “fix”.
    <div className="-mx-4 -mb-28 flex min-h-[calc(100dvh-3.5rem)] flex-col md:-mx-6 md:mb-0 md:min-h-[calc(100dvh-3.5rem)] md:flex-row lg:-mx-10 xl:-mx-12">
      <section className="flex min-h-0 w-full flex-col border-border/70 md:w-[22rem] md:border-r lg:w-[26rem]">
        {/* Intentional (accepted): pr-28 only clears the overlaid page-reference on small screens. */}
        <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2 pr-28 md:pr-3">
          <button
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
            data-control-id="learning-chat.back"
            onClick={onBack}
            type="button"
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            {t("common.back")}
          </button>
          <button
            className="rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
            data-control-id="learning-chat.session.new"
            onClick={onBack}
            type="button"
          >
            {t("learningRoute.chatNew")}
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-3 pt-2 pb-3">
          {session.messages.map((message) => (
            <ThreadItem key={message.id} message={message} />
          ))}
          {session.phase === "clarify" ? (
            <div className="pt-1">
              <LearningChatClarifyOptions
                onSelect={(id) => onAnswer(t(clarifyOptionKey(session.situationId, session.clarifyStep, id)))}
                onUnknown={() => onAnswer(t("learningRoute.chatClarify.unknown"))}
                situationId={session.situationId}
                step={session.clarifyStep}
              />
            </div>
          ) : null}
          <div ref={bottomRef} />
        </div>

        <div className="shrink-0 border-t border-border/60 px-3 pt-2 pb-3">
          <p className="mb-1.5 text-center text-[11px] leading-relaxed text-muted-foreground">
            {t("learningRoute.chatPrivacy")}
          </p>
          <LearningChatComposer
            busy={session.phase === "thinking"}
            draft={draft}
            onDraftChange={onDraftChange}
            onSend={() => {
              if (!draft.trim()) return;
              if (session.phase !== "greeting" && session.phase !== "clarify" && session.phase !== "result")
                return;
              onAnswer(draft.trim());
            }}
            placeholderKey={
              session.phase === "greeting"
                ? "learningRoute.chatPlaceholder"
                : session.phase === "clarify"
                  ? "learningRoute.chatAnswerPlaceholder"
                  : "learningRoute.chatFollowUpPlaceholder"
            }
            plusIcon
          />
        </div>
      </section>

      <section className="min-h-0 min-w-0 flex-1 bg-muted/15">
        {showThinking ? <LearningChatThinkingPanel /> : null}
        {session.phase === "result" ? <LearningChatResultPanel title={session.title} /> : null}
      </section>
    </div>
  );
}

function ThreadItem({ message }: { message: ThreadMessage }) {
  const { t } = useTranslation();
  if (message.kind === "user") {
    return (
      <div className="ml-auto w-fit max-w-[92%] rounded-2xl bg-primary/10 px-3 py-2 text-sm leading-snug text-foreground">
        {message.text}
      </div>
    );
  }
  if (message.kind === "status") {
    return (
      <div className="space-y-0.5 px-0.5 pt-0.5 text-xs leading-snug text-muted-foreground">
        <p>{t("learningRoute.chatWorkedFor", { seconds: message.seconds })}</p>
        <p className="inline-flex items-center gap-1.5">
          <span aria-hidden="true">✦</span>
          {t(message.stepKey)}
        </p>
      </div>
    );
  }
  return <p className="px-0.5 text-sm font-medium leading-snug text-foreground">{message.text}</p>;
}
